// src/pages/MyGems.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import "../index.css";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { stripHtmlToText } from "@/lib/richText";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, MessageCircle } from "lucide-react";

// 日记的模式：仅保留 spark / guided；历史 deep 统一视为 spark
type AiMode = "spark" | "guided" | null;

interface JournalRow {
  id: string;
  created_at: string;
  content: string;
  ai_mode: AiMode;
}

export default function MyGems() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [entries, setEntries] = useState<JournalRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const MONTHLY_INSIGHT_PROMPT_SNIPPET =
    "You are generating a concise 30-day summary AI insight for the user.";

  // 🔝 Back to top：直接把整页滚回最上面
  const handleBackToTop = () => {
    if (typeof window === "undefined") return;

    const el =
      document.scrollingElement ||
      document.documentElement ||
      document.body;

    el.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // 进入 My Gems 时隐藏整页滚动条，离开时恢复
  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    htmlEl.classList.add("body-hide-scrollbar");
    bodyEl.classList.add("body-hide-scrollbar");

    return () => {
      htmlEl.classList.remove("body-hide-scrollbar");
      bodyEl.classList.remove("body-hide-scrollbar");
    };
  }, []);

  // 读取 Gems 列表
  useEffect(() => {
    const fetchGems = async () => {
      setIsLoading(true);
      setErrorMsg(null);

      try {
        // 1. Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // If not logged in, just fetch with limit (or handle as empty)
          // For now, let's proceed with default behavior
        }

        // 2. Check paid subscription status
        let hasPaidPlan = false;
        if (user) {
          const { data: profile } = await supabase
            .from("user_profiles")
            .select("has_lifetime_deal, subscription_tier")
            .eq("id", user.id)
            .single();

          const paidTiers = [
            "beginner",
            "intermediate",
            "upper_intermediate",
            "lifetime_deal",
            "beginner_monthly",
            "beginner_yearly",
            "intermediate_monthly",
            "intermediate_yearly",
            "upper_intermediate_monthly",
            "upper_intermediate_yearly",
          ];

          const hasLifetimeDeal = !!profile?.has_lifetime_deal;
          const tier = (profile as any)?.subscription_tier as string | null;
          const hasPaidTier = tier ? paidTiers.includes(tier) : false;

          if (hasLifetimeDeal || hasPaidTier) {
            hasPaidPlan = true;
          }
        }

        // 3. Build query
        let query = supabase
          .from("journals")
          .select("id, created_at, content, ai_mode")
          .order("created_at", { ascending: false });

        // Only apply limit if NOT on any paid plan
        if (!hasPaidPlan) {
          query = query.limit(30);
        }

        const { data, error } = await query;

        if (error) {
          console.error("Error loading journals:", error);
          setErrorMsg(t('gems.loadError', { defaultValue: 'Something went wrong while loading your gems.' }));
          return;
        }

        const cleaned = (data ?? []).filter((row) => {
          const content = (row as any)?.content;
          return !(
            typeof content === "string" &&
            content.includes(MONTHLY_INSIGHT_PROMPT_SNIPPET)
          );
        });

        setEntries(cleaned as JournalRow[]);
      } catch (err) {
        console.error("Unexpected error loading journals:", err);
        setErrorMsg(t('gems.loadError', { defaultValue: 'Something went wrong while loading your gems.' }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchGems();
  }, []);

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-primary mb-2">{t("gems.title", { defaultValue: "My Gems" })}</h1>
        <p className="text-muted-foreground">
          {t("gems.subtitle", { defaultValue: "Your living archive of inner wisdom." })}
        </p>

        <div className="mt-4 flex justify-center">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate("/my-gems/monthly-report")}
          >
            {t("gems.monthlyReportButton", { defaultValue: "Monthly Gem Report" })}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t("gems.loading", { defaultValue: "Loading your gems…" })}
          </CardContent>
        </Card>
      ) : errorMsg ? (
        <Card>
          <CardContent className="py-12 text-center text-red-500 text-sm">
            {errorMsg}
          </CardContent>
        </Card>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {t("gems.empty", { defaultValue: "No gems yet. Capture your first reflection today." })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {entries.map((entry) => {
              // 根据 ai_mode 决定标签内容和样式
              const normalizedMode: AiMode = entry.ai_mode === "guided" ? "guided" : "spark";
              const created = new Date(entry.created_at);
              const dateTimeLabel = created.toLocaleString();
              let label = t("gems.mode.spark", { defaultValue: "Spark" });
              let variant:
                | "default"
                | "secondary"
                | "outline"
                | "destructive" = "default";
              let Icon = Sparkles;

              if (normalizedMode === "spark") {
                label = t("gems.mode.spark", { defaultValue: "Spark" });
                variant = "secondary";
                Icon = Sparkles;
              } else if (normalizedMode === "guided") {
                label = t("gems.mode.guided", { defaultValue: "Guided" });
                variant = "destructive"; // 红底白字
                Icon = MessageCircle;
              }

              return (
                <Card
                  key={entry.id}
                  className="cursor-pointer hover:shadow-[var(--shadow-soft)] transition-shadow"
                  onClick={() => navigate(`/gem/${entry.id}`)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {dateTimeLabel}
                      </CardTitle>

                      {entry.ai_mode && (
                        <Badge variant={variant} className="gap-1 px-3">
                          <Icon className="h-4 w-4" />
                          {label}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                      {stripHtmlToText(String(entry.content || ""))}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* 底部 Back to top 按钮 */}
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={handleBackToTop}
              className="mt-6 mx-auto flex items-center justify-center rounded-full border px-4 py-2 text-xs text-primary border-primary/40 hover:bg-primary/5 transition-colors"
            >
              {t('gems.backToTop', { defaultValue: 'Back to top' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
